import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { CountryCode } from 'libphonenumber-js';
import { Globe2 } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  type ContactDetailHint,
  contactDetailKinds
} from 'v5/core/contact-details/contactDetailKinds';
import { primaryEmailIndex } from 'v5/core/contact-details/primaryEmail';
import type {
  ContactDetailType,
  FieldSpecOf
} from 'v5/core/entity-schema/entitySchema.types';
import type { ContactDetailItem } from 'v5/core/entity-values/values.types';
import { rowIdUtils } from 'v5/core/form-schema/formRows';
import { Badge } from 'v5/design-system/Badge';
import { InputGroup2Button } from 'v5/design-system/InputGroup2';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger
} from 'v5/design-system/Select';
import {
  RowList,
  RowListCopy,
  RowListDisplayRow,
  RowListInput,
  RowListLabelSizer,
  RowListLead,
  RowListLeadIcon,
  RowListLeadSign,
  RowListMessage,
  RowListMessageAction,
  RowListPart,
  RowListRemove,
  RowListRow
} from 'v5/patterns/forms/row-list/RowList';
import {
  PLACEHOLDER_ROW_KEY,
  useRowListKeyboard
} from 'v5/patterns/forms/row-list/useRowListKeyboard';
import { ResourceSocialPlatformIcon } from 'v5/patterns/resource/ResourceSocialPlatformIcon';
import { contactTypeIcon } from 'v5/patterns/resource/resourceFieldIcons';
import { SocialProfileHandle } from 'v5/patterns/resource/SocialProfileHandle';
import { CountryInput } from 'v5/patterns/widgets/PhoneInput/CountryInput';
import { getCountryFlag } from 'v5/patterns/widgets/PhoneInput/country';
import { useFieldContext } from '../FieldContext';
import { UNIT_ADD_ATTRIBUTE } from '../unitPath';
import { EmptyField } from './EmptyField';
import { FieldControlBase } from './FieldControlBase';
import type {
  FieldComponent,
  FieldComponentProps
} from './fieldComponent.types';
import {
  type ContactDetailRowNote,
  type ContactDetailRowState,
  useContactDetailRows
} from './useContactDetailRows';

/** One entry of each type, as the add action and row names say it. */
const INPUT_MODE: Record<ContactDetailType, 'email' | 'tel' | 'url'> = {
  EMAIL: 'email',
  PHONE: 'tel',
  SOCIAL: 'url',
  WEBSITE: 'url'
};

const NOTE_VARIANT: Record<
  ContactDetailRowNote['type'],
  'danger' | 'attention' | 'default'
> = {
  error: 'danger',
  hint: 'attention',
  pasted: 'default',
  split: 'default'
};

export type ContactDetailsFieldConfig = {
  /** The country a new phone row reads its number in (the workspace's), or `null`. */
  useDefaultCountry(): CountryCode | null;
};

/**
 * `ContactDetailItem[]` (emails, phones, socials, websites) as rows saved
 * exactly as typed: the value first, its label after it, a phone's country
 * or a social's platform before it. An empty row at the end is the only way
 * to add; the row keyboard (`useRowListKeyboard`) does the rest. At rest the
 * rows read like a contact card: links, a copy action on hover.
 */
export function createContactDetailsField(
  config: ContactDetailsFieldConfig
): FieldComponent<'contact-details'> {
  return function ContactDetailsField(props) {
    const ctx = useFieldContext();
    const rows =
      useWatch<Record<string, ContactDetailItem[] | undefined>>({
        name: props.path
      }) ?? [];
    if (ctx.mode === 'display' && rows.length === 0) {
      return (
        <EmptyField
          spec={props.spec}
          path={props.path}
          hideLabel={props.hideLabel}
        />
      );
    }
    return (
      <FieldControlBase
        spec={props.spec}
        hideLabel={props.hideLabel}
        path={props.path}
      >
        <div>
          {ctx.mode === 'edit' ? (
            <ContactDetailsEditor {...props} config={config} />
          ) : (
            <ContactDetailsDisplay {...props} />
          )}
        </div>
      </FieldControlBase>
    );
  };
}

function errorText(
  t: TFunction<'patterns'>,
  code: Extract<ContactDetailRowNote, { type: 'error' }>['code']
): string {
  switch (code) {
    case 'platform-required':
      return t('SchemaForm.contact.error.platformRequired');
    case 'not-an-email':
      return t('SchemaForm.contact.error.notAnEmail');
    case 'not-a-website':
      return t('SchemaForm.contact.error.notAWebsite');
    case 'value-required':
      return t('SchemaForm.contact.error.valueRequired');
  }
}

function hintText(t: TFunction<'patterns'>, hint: ContactDetailHint): string {
  switch (hint.code) {
    case 'phone-unreadable':
      return t('SchemaForm.contact.hint.phoneUnreadable');
    case 'phone-incomplete':
      return t('SchemaForm.contact.hint.phoneIncomplete', {
        code: hint.callingCode
      });
    case 'phone-trunk-zero':
      return t('SchemaForm.contact.hint.phoneTrunkZero');
    case 'duplicate':
      return t('SchemaForm.contact.hint.duplicate', { row: hint.ofRow + 1 });
  }
}

/** The row that will be the resource's primary email gets a chip, once there are two to tell apart. */
function primaryIndexOf(
  spec: FieldSpecOf<'contact-details'>,
  rows: ReadonlyArray<ContactDetailItem>
): number {
  if (spec.detailType !== 'EMAIL') return -1;
  const filled = rows.filter(row => row.value.trim() !== '');
  return filled.length > 1 ? primaryEmailIndex(rows) : -1;
}

function PrimaryChip() {
  const { t } = useTranslation('patterns');
  return (
    <Badge variant="accent" tone="subtle" size="sm">
      {t('SchemaForm.contact.primary')}
    </Badge>
  );
}

function NoPlatformBadge() {
  return (
    <span
      aria-hidden="true"
      className="inline-flex size-5 items-center justify-center rounded border border-dashed border-bg-inset text-xs font-bold text-fg-muted"
    >
      ?
    </span>
  );
}

function ContactDetailsEditor({
  spec,
  path,
  config
}: FieldComponentProps<'contact-details'> & {
  config: ContactDetailsFieldConfig;
}) {
  const { t } = useTranslation('patterns');
  const ctx = useFieldContext();
  const defaultCountry = config.useDefaultCountry();
  const rows = useContactDetailRows(spec, path, defaultCountry);
  const keyboard = useRowListKeyboard({
    insert: 'end',
    rowKeys: rows.states.map(state => state.key),
    manualOrder: false,
    rowFacts: rows.rowFacts,
    onAcknowledgeHint: rows.acknowledge,
    onShowError: rows.showError,
    onRemove: rows.remove
  });
  const primaryIndex = primaryIndexOf(
    spec,
    rows.states.map(state => state.row)
  );
  const labels = spec.typeOptions.map(option => option.label);
  const hasLabel = spec.detailType !== 'SOCIAL';

  const renderMessage = (
    state: ContactDetailRowState,
    messageId: string
  ): ReactNode => {
    const { note, key } = state;
    if (note === null) return null;
    const goOn = state.acknowledged && note.type !== 'error' && (
      <span>
        <kbd>↵</kbd> {t('SchemaForm.contact.goOn')}
      </span>
    );
    switch (note.type) {
      case 'error':
        return (
          <RowListMessage tone="error" id={messageId}>
            {errorText(t, note.code)}
          </RowListMessage>
        );
      case 'pasted':
        return (
          <RowListMessage tone="info" id={messageId}>
            <span>{t('SchemaForm.contact.pasted')}</span>
            <RowListMessageAction
              onClick={() => rows.update(key, { value: note.asPasted })}
            >
              {t('SchemaForm.contact.keepAsPasted', { value: note.asPasted })}
            </RowListMessageAction>
            {goOn}
          </RowListMessage>
        );
      case 'split':
        return (
          <RowListMessage tone="info" id={messageId}>
            <span>
              {t(`SchemaForm.contact.looksLike.${spec.detailType}`, {
                count: note.parts.length
              })}
            </span>
            <RowListMessageAction onClick={() => rows.split(key, note.parts)}>
              {t('SchemaForm.contact.splitInto', { count: note.parts.length })}
            </RowListMessageAction>
            {goOn}
          </RowListMessage>
        );
      case 'hint': {
        const fix =
          note.hint.code === 'phone-trunk-zero' ? note.hint.fix : null;
        return (
          <RowListMessage tone="hint" id={messageId}>
            <span>{hintText(t, note.hint)}</span>
            {fix !== null && (
              <RowListMessageAction
                onClick={() => rows.update(key, { value: fix })}
              >
                {t('SchemaForm.contact.use', { fix })}
              </RowListMessageAction>
            )}
            {goOn}
          </RowListMessage>
        );
      }
    }
  };

  const renderRow = (state: ContactDetailRowState | null, index: number) => {
    const key = state?.key ?? rows.placeholderKey;
    const row = state?.row ?? null;
    const name = row
      ? t(`SchemaForm.contact.row.${spec.detailType}`, { index: index + 1 })
      : t(`SchemaForm.contact.add.${spec.detailType}`);
    const messageId = `${key}-message`;
    const note: ContactDetailRowNote | null = state?.note ?? null;
    const display = row
      ? contactDetailKinds[spec.detailType].display({
          value: row.value,
          type: row.type,
          country: row.country ?? null
        })
      : null;
    const international = row?.value.trim().startsWith('+') ?? false;
    const country = row ? (row.country ?? null) : defaultCountry;
    const renderLead = (): ReactNode => {
      switch (spec.detailType) {
        case 'PHONE':
          return row ? (
            <RowListPart column="lead" part="country">
              <span className="flex items-center self-stretch pr-2 pl-2.5">
                <CountryInput
                  variant="headless"
                  size={ctx.size}
                  showCallingCode={false}
                  // A +code number brings its own country.
                  readOnly={international}
                  aria-label={t('SchemaForm.contact.countryFor', {
                    row: name.toLowerCase()
                  })}
                  formProps={{
                    value: display?.country ?? country,
                    onChange: picked =>
                      rows.update(key, picked ? { country: picked } : {})
                  }}
                />
              </span>
            </RowListPart>
          ) : defaultCountry ? (
            <RowListLead>{getCountryFlag(defaultCountry)}</RowListLead>
          ) : (
            <RowListLeadSign icon={Globe2} />
          );
        case 'SOCIAL':
          return row ? (
            <Select
              value={row.type}
              onValueChange={type => rows.update(key, { type })}
            >
              <RowListPart column="lead" part="type">
                <InputGroup2Button asChild>
                  <SelectTrigger
                    variant="headless"
                    className="w-auto gap-1"
                    aria-label={t('SchemaForm.contact.platformFor', {
                      row: name.toLowerCase()
                    })}
                    aria-invalid={note?.type === 'error'}
                    aria-describedby={note ? messageId : undefined}
                  >
                    <RowListLeadIcon>
                      {row.type === '' ? (
                        <NoPlatformBadge />
                      ) : (
                        <ResourceSocialPlatformIcon platform={row.type} />
                      )}
                    </RowListLeadIcon>
                  </SelectTrigger>
                </InputGroup2Button>
              </RowListPart>
              <SelectContent>
                {spec.typeOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <RowListLead>
              <NoPlatformBadge />
            </RowListLead>
          );
        case 'EMAIL':
        case 'WEBSITE':
          return (
            <RowListLeadSign
              icon={contactTypeIcon(spec.detailType, row?.type ?? null)}
            />
          );
      }
    };

    return (
      <Fragment key={key}>
        <RowListRow
          rowKey={row ? key : PLACEHOLDER_ROW_KEY}
          fresh={row !== null && (ctx.ghost || row.rowId.type === 'local')}
          variant={note ? NOTE_VARIANT[note.type] : 'default'}
          aria-label={name}
          onBlur={event => {
            if (row === null) return;
            if (event.currentTarget.contains(event.relatedTarget)) return;
            rows.settle(key);
          }}
        >
          {renderLead()}
          <RowListInput
            key="value"
            value={row?.value ?? ''}
            inputMode={INPUT_MODE[spec.detailType]}
            spellCheck={false}
            placeholder={
              row
                ? t(`SchemaForm.contact.placeholder.${spec.detailType}`)
                : name
            }
            aria-label={name}
            aria-describedby={note ? messageId : undefined}
            aria-invalid={note?.type === 'error' && note.part === 'value'}
            aria-keyshortcuts="Enter Backspace ArrowUp ArrowDown"
            {...(row ? {} : { [UNIT_ADD_ATTRIBUTE]: '' })}
            onChange={event =>
              row
                ? rows.changeValue(row, event.target.value)
                : rows.startRow(event.target.value)
            }
            onPaste={event =>
              rows.onPaste(
                key,
                country,
                value =>
                  row ? rows.changeValue(row, value) : rows.startRow(value),
                event
              )
            }
          />
          {row && hasLabel && (
            <Select
              value={row.type}
              onValueChange={type => rows.update(key, { type })}
            >
              <RowListPart column="label" part="type">
                <InputGroup2Button asChild>
                  <SelectTrigger
                    variant="headless"
                    className="w-auto gap-1 text-fg-muted"
                    aria-label={t('SchemaForm.contact.labelFor', {
                      row: name.toLowerCase()
                    })}
                  >
                    <RowListLabelSizer
                      options={labels}
                      mark={index === primaryIndex && <PrimaryChip />}
                    >
                      {spec.typeOptions.find(o => o.value === row.type)
                        ?.label ?? t('SchemaForm.contact.label')}
                    </RowListLabelSizer>
                  </SelectTrigger>
                </InputGroup2Button>
              </RowListPart>
              <SelectContent>
                {spec.typeOptions.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {row && (
            <RowListRemove
              aria-label={t('SchemaForm.contact.remove', {
                what: row.value.trim() || name.toLowerCase()
              })}
              onClick={() => rows.remove(key)}
            />
          )}
        </RowListRow>
        {state && renderMessage(state, messageId)}
      </Fragment>
    );
  };

  return (
    <RowList
      tone={ctx.variant}
      size={ctx.size}
      aria-label={spec.label}
      {...keyboard.listProps}
    >
      {[
        ...rows.states.map((state, index) => renderRow(state, index)),
        renderRow(null, rows.states.length)
      ]}
    </RowList>
  );
}

function ContactDetailsDisplay({
  spec,
  path
}: FieldComponentProps<'contact-details'>) {
  const { t } = useTranslation('patterns');

  const ctx = useFieldContext();
  const form = useFormContext<Record<string, ContactDetailItem[]>>();
  const rows = useWatch({ control: form.control, name: path }) ?? [];
  const primaryIndex = primaryIndexOf(spec, rows);
  const labels = spec.typeOptions.map(option => option.label);
  return (
    <RowList role="list" tone="plain" size={ctx.size} aria-label={spec.label}>
      {rows.map((row, index) => {
        const display = contactDetailKinds[spec.detailType].display({
          value: row.value,
          type: row.type,
          country: row.country ?? null
        });
        const text = display.profile ? (
          <SocialProfileHandle profile={display.profile} />
        ) : (
          display.text
        );
        const external = display.href?.startsWith('http') ?? false;
        return (
          <RowListDisplayRow key={rowIdUtils.key(row.rowId)} role="listitem">
            {spec.detailType === 'PHONE' &&
              (display.country ? (
                <RowListLead>{getCountryFlag(display.country)}</RowListLead>
              ) : (
                <RowListLeadSign icon={Globe2} />
              ))}
            {(spec.detailType === 'EMAIL' || spec.detailType === 'WEBSITE') && (
              <RowListLeadSign
                icon={contactTypeIcon(spec.detailType, row.type)}
              />
            )}
            {spec.detailType === 'SOCIAL' && (
              <RowListLead>
                <ResourceSocialPlatformIcon
                  platform={display.platform ?? 'other'}
                />
              </RowListLead>
            )}
            <RowListPart column="value">
              <span className="truncate pr-2 font-semibold tabular-nums">
                {display.href ? (
                  <a
                    href={display.href}
                    className="hocus:text-brand-500 hocus:underline"
                    {...(external
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                  >
                    {text}
                  </a>
                ) : (
                  text
                )}
              </span>
            </RowListPart>
            {spec.detailType !== 'SOCIAL' && (
              <RowListPart column="label">
                <span className="pr-2 text-fg-muted">
                  <RowListLabelSizer
                    options={labels}
                    mark={index === primaryIndex && <PrimaryChip />}
                  >
                    {spec.typeOptions.find(o => o.value === row.type)?.label ??
                      ''}
                  </RowListLabelSizer>
                </span>
              </RowListPart>
            )}
            <RowListCopy
              value={row.value}
              aria-label={t('SchemaForm.list.copy', { text: row.value })}
            />
          </RowListDisplayRow>
        );
      })}
    </RowList>
  );
}
